import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  IconButton,
  Tooltip,
  useMediaQuery,
  Grid,
  Box,
  Tab,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Typography,
  Chip,
} from '@mui/material'
import RemoveRedEyeRoundedIcon from '@mui/icons-material/RemoveRedEyeRounded'
import CloseIcon from '@mui/icons-material/Close'
import LinkIcon from '@mui/icons-material/Link'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { useTheme } from '@mui/system'
import { brandColors, gradientColors, textColors } from '../../../Common/Color/Color'
import { fontSizes } from '../../../Common/Typography/TypographyStyles'
import { Task } from '../../../../redux/task/types'
import TaskBadge from '../TaskTableComponents/TaskBadge'
import { setDateFormat, setTimeFormat } from '../../../../utils/dateUtils'

interface TaskManageViewModalProps {
  task: Task
}

const TaskManageViewModal: React.FC<TaskManageViewModalProps> = ({ task }) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('1')

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue)
  }

  // Function to get display value for original selections
  const getOriginalSelectionDisplay = (
    originalSelection: string[] | undefined,
    currentValues: any[] | string[] | undefined,
    type: 'operationalManager' | 'functionalArea' | 'supervisor'
  ): string => {
    // Check if original selection includes "all" or specific ALL_ constants
    if (originalSelection) {
      const hasAllSelection = originalSelection.some(selection => 
        selection === 'all' || 
        selection === 'ALL_SUPERVISORS' || 
        selection === 'ALL_OPERATIONAL_MANAGERS' || 
        selection === 'ALL_FUNCTIONAL_AREAS'
      )
      
      if (hasAllSelection) {
        // Match the exact display logic from TaskAssignment component
        if (type === 'operationalManager') {
          return 'All Operations/Practice Manager'
        } else if (type === 'functionalArea') {
          return 'All Functional Area'
        } else if (type === 'supervisor') {
          return 'All Supervisors'
        }
        return 'All'
      }
    }
    
    // If no original selection, fall back to current values
    if (!originalSelection) {
      if (!currentValues || currentValues.length === 0) {
        return 'Not specified'
      }
      
      if (type === 'operationalManager' || type === 'supervisor') {
        return (currentValues as any[]).map(item => item.name || item.workdayId).join(', ')
      } else {
        return (currentValues as string[]).join(', ')
      }
    }
    
    // Display original selection (filter out ALL_ constants)
    const filteredSelection = originalSelection.filter(selection => 
      !selection.startsWith('ALL_') && selection !== 'all'
    )
    
    if (filteredSelection.length === 0) {
      return 'Not specified'
    }
    
    // For operational managers and supervisors, try to map workday IDs to names
    if (type === 'operationalManager' || type === 'supervisor') {
      return filteredSelection.map(selection => {
        // Try to find the name from current values
        if (currentValues && Array.isArray(currentValues)) {
          const found = currentValues.find(cv => 
            (typeof cv === 'object' && cv?.workdayId === selection) ||
            (typeof cv === 'string' && cv === selection)
          )
          if (found && typeof found === 'object' && found?.name) {
            return found.name
          }
        }
        return selection
      }).join(', ')
    }
    
    return filteredSelection.join(', ')
  }

  const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return ''
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date
      if (isNaN(dateObj.getTime())) return ''
      return `${setDateFormat(dateObj.toISOString())} ${setTimeFormat(dateObj.toISOString())}`
    } catch (error) {
      console.error('Error formatting date:', error)
      return ''
    }
  }

  const formatUserName = (email: string | undefined) => {
    if (!email) return 'N/A'
    try {
      // Extract the part before @ and format it
      const namePart = email.split('@')[0]
      // Replace dots and underscores with spaces, then capitalize each word
      return namePart
        .replace(/[._]/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    } catch (error) {
      console.error('Error formatting user name:', error)
      return email // Fallback to email if formatting fails
    }
  }

  const CustomizeTabPanel: React.FC<{ value: string; children: React.ReactNode }> = ({ value, children }) => {
    return (
      <TabPanel value={value}>
        <Box
          sx={{
            ...(!fullScreen && {
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#5f2b7f',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: '#555',
              },
              scrollbarWidth: 'auto',
              msOverflowStyle: 'auto',
            }),
          }}
        >
          {children}
        </Box>
      </TabPanel>
    )
  }

  return (
    <React.Fragment>
      <Tooltip title='View' arrow>
        <IconButton 
          color='success' 
          size='small' 
          onClick={handleOpen}
          data-testid={`task-manage-view-button-${task.id}`}
        >
          <RemoveRedEyeRoundedIcon />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        aria-labelledby='task-manage-dialog-title'
        fullScreen={fullScreen}
        sx={{
          '& .MuiDialog-paper': {
            width: fullScreen ? '100%' : '500px',
            maxWidth: fullScreen ? '100%' : '500px',
            height: fullScreen ? '100%' : 'auto',
            maxHeight: fullScreen ? '100%' : '80vh',
          },
        }}
      >
        <Box sx={{ background: gradientColors.greenGradient, position: 'relative' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', padding: '16px 48px 16px 24px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <DialogTitle
                sx={{
                  color: textColors.white,
                  fontWeight: 'bold',
                  fontSize: fontSizes.size20,
                  padding: 0,
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-all',
                  whiteSpace: 'normal',
                  flex: 1,
                }}
              >
                {task.title}
              </DialogTitle>
              <TaskBadge value={task.importance || 'low'} badgeType='importanceBadge' />
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              color: textColors.white,
            }}
            size='small'
            data-testid={`task-manage-view-close-button-${task.id}`}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Box sx={{ width: '100%', p: 2 }}>
          <TabContext value={value}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList onChange={handleChange} aria-label='Task tabs' centered>
                <Tab label='Task Details' value='1' />
                <Tab label='Assignment' value='2' />
              </TabList>
            </Box>
            <CustomizeTabPanel value='1'>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box>
                  <Grid container spacing={2} direction='column' sx={{ marginTop: '16px' }}>
                    <Grid container spacing={2} direction='column'>
                      {/* Task Description */}
                      <Box display='flex' alignItems='center' sx={{ marginBottom: '8px' }}>
                        <FormControl variant='outlined' fullWidth>
                          <InputLabel 
                            htmlFor='task-description-input-field'
                            sx={{ 
                              backgroundColor: 'white',
                              paddingX: '4px',
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}
                          >
                            Task Description
                          </InputLabel>
                          <OutlinedInput
                            id='task-description-input-field'
                            type='text'
                            value={task.details || ''}
                            readOnly
                            label='Task Description'
                            multiline
                            rows={4}
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                              },
                            }}
                          />
                        </FormControl>
                      </Box>

                      {/* Task Link */}
                      <Box display='flex' alignItems='center' sx={{ marginBottom: '8px' }}>
                        <FormControl variant='outlined' fullWidth>
                          <InputLabel 
                            htmlFor='link-input-field'
                            sx={{ 
                              backgroundColor: 'white',
                              paddingX: '4px',
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}
                          >
                            Task Link
                          </InputLabel>
                          <OutlinedInput
                            id='link-input-field'
                            type='text'
                            value={task.link || ''}
                            readOnly
                            label='Task Link'
                            onClick={() => task.link && task.link.trim() !== '' && window.open(task.link, '_blank', 'noopener,noreferrer')}
                            sx={{
                              cursor: task.link && task.link.trim() !== '' ? 'pointer' : 'default',
                              '&:hover': {
                                backgroundColor: task.link && task.link.trim() !== '' ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                              },
                              '& .MuiOutlinedInput-input': {
                                cursor: task.link && task.link.trim() !== '' ? 'pointer' : 'default',
                                color: task.link && task.link.trim() !== '' ? brandColors.purpleTelus : 'inherit',
                                textDecoration: task.link && task.link.trim() !== '' ? 'underline' : 'none',
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                              },
                            }}
                            startAdornment={
                              <InputAdornment position='start'>
                                <LinkIcon />
                              </InputAdornment>
                            }
                          />
                        </FormControl>
                      </Box>

                      {/* Due Date */}
                      <Box display='flex' alignItems='center' sx={{ marginBottom: '8px' }}>
                        <FormControl variant='outlined' fullWidth>
                          <InputLabel 
                            htmlFor='due-date-input-field-task-tab'
                            sx={{ 
                              backgroundColor: 'white',
                              paddingX: '4px',
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}
                          >
                            Due Date
                          </InputLabel>
                          <OutlinedInput
                            id='due-date-input-field-task-tab'
                            type='text'
                            value={formatDate(task.dueDate)}
                            readOnly
                            label='Due Date'
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                              },
                            }}
                          />
                        </FormControl>
                      </Box>

                      {/* Created By or Last Updated By - Event Style (Task Tab) */}
                      <Grid container justifyContent='flex-end'>
                        <Box display='flex' flexDirection='column' alignItems='flex-end' sx={{ paddingTop: 1, paddingRight: 2, paddingLeft: 2, paddingBottom: 0 }}>
                          {task.updatedBy?.email ? (
                            <>
                              <Typography variant='body1' fontWeight='bold'>
                                Last Updated By
                              </Typography>
                              <Typography variant='body2' color='textSecondary'>
                                {formatUserName(task.updatedBy?.email)}
                              </Typography>
                              <Typography variant='body2' color='textSecondary'>
                                {formatDate(task.updatedAt)}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Typography variant='body1' fontWeight='bold'>
                                Created By
                              </Typography>
                              <Typography variant='body2' color='textSecondary'>
                                {formatUserName(task.createdBy?.email)}
                              </Typography>
                              <Typography variant='body2' color='textSecondary'>
                                {formatDate(task.createdAt)}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </CustomizeTabPanel>
            <CustomizeTabPanel value='2'>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box>
                  <Grid container spacing={2} direction='column' sx={{ marginTop: '16px' }}>
                    <Grid container spacing={2} direction='column'>

                      {/* Documentation Status - Always show */}
                      <Box display='flex' alignItems='center' sx={{ marginBottom: '16px' }}>
                        {task.required ? (
                          <Chip
                            label="Documentation Required"
                            size="small"
                            sx={{
                              backgroundColor: '#e12339',
                              color: 'white',
                              fontWeight: 'bold',
                              '& .MuiChip-label': {
                                fontSize: '0.75rem',
                              },
                            }}
                          />
                        ) : (
                          <Chip
                            label="Documentation Optional"
                            size="small"
                            sx={{
                              backgroundColor: '#3366cc',
                              color: 'white',
                              fontWeight: 'bold',
                              '& .MuiChip-label': {
                                fontSize: '0.75rem',
                              },
                            }}
                          />
                        )}
                      </Box>

                      {/* Operational Manager */}
                      <Box display='flex' alignItems='center' sx={{ marginBottom: '8px' }}>
                        <FormControl variant='outlined' fullWidth>
                          <InputLabel 
                            htmlFor='operational-manager-input-field'
                            sx={{ 
                              backgroundColor: 'white',
                              paddingX: '4px',
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}
                          >
                            Operations/Practice Manager
                          </InputLabel>
                          <OutlinedInput
                            id='operational-manager-input-field'
                            type='text'
                            value={getOriginalSelectionDisplay(
                              task.originalOperationalManagerSelection,
                              task.operationalManager,
                              'operationalManager'
                            )}
                            readOnly
                            label='Operational Manager'
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                              },
                            }}
                          />
                        </FormControl>
                      </Box>

                      {/* Functional Area */}
                      <Box display='flex' alignItems='center' sx={{ marginBottom: '8px' }}>
                        <FormControl variant='outlined' fullWidth>
                          <InputLabel 
                            htmlFor='functional-area-input-field'
                            sx={{ 
                              backgroundColor: 'white',
                              paddingX: '4px',
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}
                          >
                            Functional Area
                          </InputLabel>
                          <OutlinedInput
                            id='functional-area-input-field'
                            type='text'
                            value={getOriginalSelectionDisplay(
                              task.originalFunctionalAreaSelection,
                              task.functionalArea,
                              'functionalArea'
                            )}
                            readOnly
                            label='Functional Area'
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                              },
                            }}
                          />
                        </FormControl>
                      </Box>

                      {/* Supervisor */}
                      <Box display='flex' alignItems='center' sx={{ marginBottom: '8px' }}>
                        <FormControl variant='outlined' fullWidth>
                          <InputLabel 
                            htmlFor='supervisor-input-field'
                            sx={{ 
                              backgroundColor: 'white',
                              paddingX: '4px',
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}
                          >
                            Supervisor
                          </InputLabel>
                          <OutlinedInput
                            id='supervisor-input-field'
                            type='text'
                            value={getOriginalSelectionDisplay(
                              task.originalSupervisorSelection,
                              task.supervisor,
                              'supervisor'
                            )}
                            readOnly
                            label='Supervisor'
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                              },
                            }}
                          />
                        </FormControl>
                      </Box>

                      {/* Last Updated By or Created By - Event Style (Assignment Tab) */}
                      <Grid container justifyContent='flex-end'>
                        <Box display='flex' flexDirection='column' alignItems='flex-end' sx={{ paddingTop: 1, paddingRight: 2, paddingLeft: 2, paddingBottom: 0 }}>
                          {task.updatedBy?.email ? (
                            <>
                              <Typography variant='body1' fontWeight='bold'>
                                Last Updated By
                              </Typography>
                              <Typography variant='body2' color='textSecondary'>
                                {formatUserName(task.updatedBy?.email)}
                              </Typography>
                              <Typography variant='body2' color='textSecondary'>
                                {formatDate(task.updatedAt)}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Typography variant='body1' fontWeight='bold'>
                                Created By
                              </Typography>
                              <Typography variant='body2' color='textSecondary'>
                                {formatUserName(task.createdBy?.email)}
                              </Typography>
                              <Typography variant='body2' color='textSecondary'>
                                {formatDate(task.createdAt)}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </CustomizeTabPanel>
          </TabContext>
        </Box>
      </Dialog>
    </React.Fragment>
  )
}

export default TaskManageViewModal
